import { useRouter } from "next/router";
import { useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { scmpPlus } from "scmp-sdk";

import { data as appData } from "shared/data";
import { useCanonicalUrl } from "shared/hooks";

import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { LetterList } from "scmp-app/components/letter-list";
import { getLetterFromQueryString } from "scmp-app/components/letter-list/helpers";
import { PageSeo } from "scmp-app/components/meta";
import { TopicList } from "scmp-app/components/topic/topic-list";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { page } from "scmp-app/pages/topics";
import type {
  StringFilterOperator,
  topicsPageQuery,
} from "scmp-app/queries/__generated__/topicsPageQuery.graphql";

import { MainHeading, PageContainer, PageContent, SubHeading } from "./styles";

const pageQuery = graphql`
  query topicsPageQuery(
    $name: String!
    $operator: StringFilterOperator = START_WITH
    $scmpPlusTypes: [String]
    $published: Boolean = true
    $enabled: Boolean = true
    $first: Int
    $after: String
    $applicationId: String!
  ) {
    ...topicListQuery @arguments(scmpPlusTypes: $scmpPlusTypes)
  }
`;

const TopicsPage: RelayComponent<unknown, topicsPageQuery> = ({ preloadedQuery }) => {
  const { pathname, query } = useRouter();
  const data = usePreloadedQuery(pageQuery, preloadedQuery);
  const selectedLetter = getLetterFromQueryString(query.char);

  const handleGetPageRoute = useMemo(() => page.route, []);

  // SEO enhancement, canonicalizing all /topics?char=... pages to /topics
  const { canonicalUrl } = useCanonicalUrl(pathname);

  return (
    <>
      <PageContainer>
        <PageContent>
          <PageSeo canonical={canonicalUrl} title="All Topics" />
          <MainHeading>Topics</MainHeading>
          <LetterList route={handleGetPageRoute} />
          <SubHeading>Topic - {selectedLetter}</SubHeading>
          <TopicList reference={data} />
        </PageContent>
      </PageContainer>
      <AppFooter />
    </>
  );
};

TopicsPage.displayName = "TopicsPage";

export default withRelay(TopicsPage, pageQuery, {
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  variablesFromContext(context) {
    const queryVariables = {
      applicationId: appData.application.scmp.entityId,
      first: 200,
      scmpPlusTypes: [scmpPlus.topicType.newsAgenda],
    };

    if (context.query.char === "others")
      return {
        ...queryVariables,
        name: String.raw`^[\d|\W]`,
        operator: "REGEX" as StringFilterOperator,
      };

    return {
      ...queryVariables,
      name: getLetterFromQueryString(context.query.char),
    };
  },
});
